<template>
    <div id="pathways-push-segment-summary" class="my-3">
        <div class="pathways-push-segment-summary__text d-flex align-items-center">
            <div class="d-flex flex-column gap-1 flex-grow-1">
                <div>
                    <h4>Pathway Summary</h4>
                    <p>Below is a summary of the pathway you've identified. Click on settings within <em>Advanced Settings</em> to map additional columns or to change how the systems are connected.</p>
                </div>
            </div>
            <div>
                <button v-if="if_download_records_finished && allColumnsMapped() && !isFile() && !isModeledAudience()" class="btn btn-white text-nowrap" @click.prevent="exportSegment()">
                    <img class="icon" src="/img/icons/avid-icons/document-download.svg" alt="">Download Records
                </button>
                <button v-else-if="allColumnsMapped() && !isFile() && !isModeledAudience()" class="btn btn-white text-nowrap" disabled>
                    <div class="spinner-border spinner-border-sm" role="status"><span class="visually-hidden"></span></div> Export Records
                </button>
            </div>
        </div>

        <div class="pathways-push-segment-summary__flow-chart row">
            <div class="col-lg-4 col-12 d-flex pe-lg-0 mb-0">
                <div class="card summary-card" id="summary-card-segment">
                    <div class="card-body">
                        <div>
                            <img class="summary-card-icon" src="/img/logo_png/oauth.jpg">
                        </div>
                        
                        <div>
                            <p>Audience</p>
                            <h4>{{ segment_selected.name }}</h4>
                            <small v-if="segment_selected.dataset">
                                <span class="badge">{{ segment_selected.dataset.name}}</span>
                            </small>
                            <small v-else-if="segment_selected.datablend">
                                <span class="badge">{{ segment_selected.datablend.name}}</span>
                            </small>
                            <small v-else-if="segment_selected.data_source">
                                <span class="badge">{{ segment_selected.data_source.name}}</span>
                            </small>
                        </div>
                    </div>
                </div>

                <figure class="d-flex flex-column justify-content-center m-0 fs-2">
                    <i class="connector-1"></i>
                </figure>
            </div>

            <div class="col-lg-4 col-12 px-lg-0 mb-0">
                <div class="card summary-card" id="summary-info">
                    <div class="card-body">
                        <div>
                            <p>Sync Frequency</p>
                            <h4>{{isFile() ? 'One Time' : 'Daily'}}</h4>
                        </div>                 
                        <div>   
                            <p>Records</p>
                            <h4 v-if="segment_size !== null && segment_size >= 0 && !isModeledAudience()">{{ number_with_zero(segment_size) }}
                                <img class="info-icon" src="/img/icons/avid-icons/info.svg" data-bs-toggle="tooltip" title="This record count is based upon the distinct values of the Primary Key Column selected below. Open 'Advanced Settings' and then 'Column Mapping' to adjust this." alt=""></img>
                            </h4>
                            <div v-else-if="segment_size === -1 && !isModeledAudience()">
                                <em>Needs More Info</em> <img class="info-icon" src="/img/icons/avid-icons/info.svg" data-bs-toggle="tooltip" title="You need to select a primary key column below before an estimated number of records can be calculated." alt=""></img>
                            </div>
                            <div v-else-if="isModeledAudience()">
                                <em>--</em>
                            </div>
                            <div v-else>
                                <div class="d-inline-block spinner-border spinner-border-sm ms-2" role="status"></div>  
                                <em>Loading</em>
                            </div>
                        </div> 
                    </div>
                </div>
            </div>

            <div class="col-lg-4 col-12 d-flex ps-lg-0 ">
                <figure class="d-flex flex-column justify-content-center m-0 fs-2">
                    <i class="fa-sharp fas fa-arrow-right m-0"></i>
                </figure>
                <div class="card summary-card" id="summary-card-pathway">
                    <div class="card-body">
                        <div>
                            <img class="summary-card-icon icon" v-if="!isFile()" :src="local_pathway_selected.integration.logo_url">
                            <img class="summary-card-icon icon p-2" v-else src="/img/icons/avid-icons/document-download.svg" alt="">
                        </div>
                        
                        <div>
                            <p>Pathway</p>
                            <h4>{{ isFile() ? 'File Export' : local_pathway_selected.name }}</h4>
                        </div>
                    </div>
                </div>
            </div>

        </div>

        <div class="card mb-2">
            <div class="card-body">
                <h4 class="mb-2">Filters Applied:</h4>
                <div v-if="census_connection.filter_description">
                    <div class="d-flex align-items-start mb-2">
                        <input type="checkbox" class="form-check-input mt-1 me-2" disabled checked  v-if="census_connection.filter_description" aria-label="Checkbox">
                        <div>
                            <strong  v-if="census_connection.filter_description">Audience Definition:</strong>
                            <span v-html="segment_selected.description" class="mb-0"></span>
                        </div>
                    </div>
                    <div class="d-flex align-items-start" v-if="census_connection.filter_description">
                        <input type="checkbox" class="form-check-input mt-1 me-2" id="include_census_filter" 
                            v-model="include_census_filter" @change="loadConnectionDetails()" aria-label="Include Census Filter">
                        <label for="include_census_filter">
                            <strong>Default Pathway Filter:</strong>
                            <span v-html="census_connection.filter_description" class="mb-0"></span>
                        </label>
                    </div>
                </div>
                <div v-else>
                    <p v-html="segment_selected.description"></p>
                </div>
            </div>
        </div>
        
        <div v-if="segment_selected.data_source && census_connection.is_file" >
            <file-column-mapping :client="client" :source="local_census_connection"
                :source_columns="source_fields" :destination="local_pathway_selected"
                v-model="local_census_connection.mappings" @prioritize="updatePrioritization"
                @changeMappingDetails="changeMappingDetails"
                ref="file_mapping"></file-column-mapping>
        </div>


        <div v-if="segment_selected.data_source && census_connection.is_file == false && !isModeledAudience()" id="advanced-settings-section" class="card">
            <div class="d-flex flex-column gap-1">
                <h4>Advanced Settings</h4>
                <p>Customize how you want to push this audience to pathway</p>
            </div>

            <transition-group v-if="if_advanced_setting_ready" tag="div" class="d-flex flex-column justify-content-start gap-3">
                <div class="accordion" key="advanced-setting-information">
                    <div class="accordion-item">
                        <div class="accordion-header">
                            <button class="accordion-button collapsed" type="button"  
                                :data-bs-target="'#advanced-setting-information'" data-bs-toggle="collapse"
                                aria-expanded="false" :aria-controls="'advanced-setting-information'" >
                                <h4>Information</h4>
                            </button>                                        
                        </div>
                        <div class="accordion-body accordion-collapse pt-0" id="advanced-setting-information" :class="if_expand_info_section ? '': 'collapse'">
                            <div class="d-flex flex-column gap-2 mt-2">
                                <!-- https://www.facebook.com/customaudiences/value_based/tos/?act=1474160189570282 -->
                                <p>Before data can be sent to {{ local_pathway_selected.name }}, you first need to align
                                        the columns to those available in this data source and decide how the data should be kept 
                                        in sync on an ongoing basis. 
                                    <!-- <span v-if="this.census_connection.source == 'data_blend'">
                                        If there is information missing from the data source, <a :href="'/integrations/'+client.url+'/mapping/data_set/'+data_set_id +'?step=3'">you can click here to map additional columns to that source</a>.
                                    </span> -->
                                </p>
                                <div class="row">
                                    <div class="col-md-6 col-12">
                                        <label>Destination Object</label>
                                        <div class="input-group">
                                            <select class="form-select" :value="effectiveDestinationObject"
                                                @change="onDestinationObjectChange($event.target.value)"
                                                :disabled="normalizedDestinationObjects.length <= 1"
                                                :class="{'is-invalid' : information_errors.destination_object !== ''}" aria-label="Destination Object">
                                                <option v-for="obj in normalizedDestinationObjects" :key="obj.full_name"
                                                    :value="obj.full_name">
                                                    {{ obj.label }}
                                                </option>
                                            </select>
                                            <button v-if="shouldShowRefreshButton()" class="btn btn-secondary" @click="refreshColumns()" 
                                                data-bs-toggle="tooltip" 
                                                title="If you don't see your list available, refresh to get the latest information." 
                                                type="button">
                                                <i class="fas fa-sync-alt"></i>
                                            </button>
                                        </div>
                                        <div class="text-danger invalid-feedback" v-if="information_errors.destination_object !== ''">
                                            {{information_errors.destination_object}}
                                        </div>
                                        <div v-if="showConversionActionPicker" class="mt-2">
                                            <label class="form-label">Conversion Action</label>
                                            <select class="form-select" :value="selectedConversionActionId" @change="onConversionActionChange($event.target.value)"
                                                :class="{'is-invalid' : information_errors.conversion_action !== ''}" aria-label="Conversion Action">
                                                <option value="">Select a conversion action</option>
                                                <option v-for="ca in conversionActions" :key="ca.id" :value="ca.id">{{ ca.name }}</option>
                                            </select>
                                            <div class="text-danger invalid-feedback" v-if="information_errors.conversion_action !== ''">
                                                {{information_errors.conversion_action}}
                                            </div>
                                        </div>
                                    </div>
                                    <div v-if="operations.length > 1" class="col-md-6 col-12 mt-2 mt-md-0">
                                        <label>Method of Sync</label>
                                        <select class="form-select" v-model="local_census_connection.operation"
                                            :class="{'is-invalid' : information_errors.operation !== ''}"  aria-label="Method of Sync">
                                            <option v-for="ops in operations" :key="ops" :value="ops">{{getOperationExplanation(ops)}}</option>
                                        </select>
                                        <div class="text-danger invalid-feedback" v-if="information_errors.operation !== ''">
                                            {{information_errors.operation}}
                                        </div>
                                    </div>
                                </div>
                            </div>        
                        </div>
                    </div>
                </div>
        
        <div class="accordion" key="advanced-setting-primary-key"
                    v-if="local_census_connection.destination_object && local_census_connection.operation || normalizedDestinationObjects.length === 1">
                    <div class="accordion-item">
                        <div class="accordion-header">
                            <button class="accordion-button collapsed" type="button"
                                :data-bs-target="'#advanced-setting-primary-key'" data-bs-toggle="collapse"
                                aria-expanded="false" :aria-controls="'advanced-setting-primary-key'" >
                                <h4>Column Mapping</h4>
                            </button>                                        
                        </div>
                        <div class="accordion-body accordion-collapse pt-0" id="advanced-setting-primary-key"  :class="if_expand_column_mapping_section ? '': 'collapse'">
                            <div class="d-flex flex-column gap-md-3 gap-0 mt-2" v-if="local_census_connection.destination_object !== null">
                                <pathways-column-mapping :client="client" :source="local_census_connection"
                                    :source_columns="source_fields" :destination="local_pathway_selected"
                                    :dest_columns="destination_fields" v-model="local_census_connection.mappings"
                                    @refresh="refreshColumns()" @prioritize="updatePrioritization"
                                    @changeMappingDetails="changeMappingDetails" :show_unmapped_only="show_unmapped_only"
                                    ref="column_mapping"></pathways-column-mapping>

                                <!-- Buttons -->
                                <div class="my-2 d-flex flex-row gap-2">
                                    <button class="btn btn-secondary" @click="addColumnMapping()" v-if="!show_unmapped_only"><i class="fas fa-plus"></i> Add Column Mapping</button>
                                    <button class="btn btn-secondary" v-else @click.prevent="show_unmapped_only=false">Show All Mapped Columns</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </transition-group>
            
            <div v-else>
                <div class="d-inline-block spinner-border spinner-border-sm ms-2" role="status"><span class="visually-hidden"></span></div>
                <p>Loading Advanced Settings Options...</p>
            </div>
        </div>

        <div v-if="instructions != null" class="mt-3 mb-3">
            <div class="alert alert-danger" role="alert" v-html="instructions"></div>
        </div>

    </div>
</template>

<script>
import { defineComponent } from 'vue';
import Swal from 'sweetalert2';

export default defineComponent({
    components: {
        Swal
    },
    props: ['segment_selected', 'pathway_selected', 'client', 'census_connection', 'information_errors'],
    data() {
        return {    
            if_advanced_setting_ready: false,
            local_census_connection: {
                mappings: [], // Initialize mappings as empty array
                destination_object: null,
                operation: null,
                id: null,
                source: null,
                is_file: false
            },
            all_column_mappings:[],
            local_pathway_selected: {
                census_destination_objects: []
            },
            source_fields: null,
            operations: [],
            destination_fields: [],
            if_download_records_finished: true,
            show_unmapped_only: false,
            if_expand_info_section: true,
            if_expand_column_mapping_section: true,
            primary_identifier: null,
            instructions: null,
            segment_size: null,
            include_census_filter: true,
            orig_include_census_filter: true,
            conversionActions: []
        };
    },
    computed: {
        normalizedDestinationObjects() {
            const objs = this.local_pathway_selected?.census_destination_objects;
            console.log(objs);
            if (!objs) return [];
            return Array.isArray(objs) ? objs : Object.values(objs);
        },
        effectiveDestinationObject() {
            const obj = this.local_census_connection?.destination_object;
            if (!obj) return obj;
            if (obj === 'user_data' || obj === 'enhanced_conversion') return obj;
            try {
                const parsed = typeof obj === 'string' ? JSON.parse(obj) : obj;
                if (parsed && (parsed.conversion_action_id || parsed.conversionActionId)) {
                    return 'enhanced_conversion';
                }
            } catch (_) {}
            return obj;
        },
        showConversionActionPicker() {
            return this.local_pathway_selected?.integration?.display_name === 'Google Ads' &&
                this.effectiveDestinationObject === 'enhanced_conversion';
        },
        selectedConversionActionId() {
            const obj = this.local_census_connection?.destination_object;
            if (!obj) return '';
            try {
                const parsed = typeof obj === 'string' ? JSON.parse(obj) : obj;
                return parsed?.conversion_action_id || parsed?.conversionActionId || '';
            } catch (_) {
                return '';
            }
        }
    },
    beforeMount() { 
        this.local_census_connection = {
            ...JSON.parse(JSON.stringify(this.census_connection)),
            mappings: this.census_connection?.mappings || []
        };
        
        //Set the filter variable
        if(this.census_connection?.syncs?.length > 0) {
            for(var i = 0; i < this.census_connection.syncs.length; i++) {
                if(this.census_connection.syncs[i].segment_id == this.segment_selected.id) {
                    this.include_census_filter = !this.census_connection.syncs[i].ignore_filter;
                    this.orig_include_census_filter = this.include_census_filter;
                    break;
                }
            }
        }
        this.local_pathway_selected = this.pathway_selected;
        this.segment_size = this.getSize(this.segment_selected.sizes);
        this.loadConnectionDetails();
        
    },
    watch: {
        pathway_selected() {
            this.local_pathway_selected = this.pathway_selected;
            this.if_advanced_setting_ready = false;
        }
    },
    mounted() {
        this.initializeTooltips();
    },
    beforeUnmount() {
        
        // Clean up Pusher listener
        if (window.Echo) {
            window.Echo.private('client.' + this.client.id)
                .stopListening('DestinationObjectsRefreshed');
        }
        
        // Clean up tooltips when component is unmounted
        const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
        tooltipTriggerList.forEach(tooltipTriggerEl => {
            const tooltip = window.bootstrap.Tooltip.getInstance(tooltipTriggerEl);
            if (tooltip) {
                tooltip.dispose();
            }
        });
        
    },
    methods: {
        initializeTooltips() {
            const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
            const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => new window.bootstrap.Tooltip(tooltipTriggerEl));
            
            for (var i = 0; i < tooltipTriggerList.length; i++) {
                (function(index) {
                    tooltipTriggerList[index].addEventListener('click', function() {
                        tooltipList[index].hide();
                    });
                    tooltipTriggerList[index].addEventListener('mouseleave', function() {
                        tooltipList[index].hide();
                    });
                })(i);
            }
        },
        isFile() {
            return this.local_pathway_selected.integration_id === "71b0c3b0-8390-49ec-9984-1c04b7384d38";
        },
        isModeledAudience() {
            return this.segment_selected.type === "modeled";
        },
        getSize(sizes) {
            //If it hasn't been sent, just show 0
            if(!sizes || this.local_census_connection === null)
                return null;

            let census_conn_id = this.local_census_connection.id;

            //Get one that is specific to this census connection
            for (var i = 0; i < sizes.length; i++){
                //Get the default size if it matches or is a data_set
                if (sizes[i].id === census_conn_id || ((census_conn_id === null || census_conn_id === undefined) && sizes[i].id === 0 && this.local_census_connection.source === "data_set"))
                    return sizes[i].size;
            }

            return null;
        },
        exportSegment() {
            this.if_download_records_finished = false;

            var form_data = {
                segment_id: this.segment_selected.id,
                census_connection: this.local_census_connection,
                ignore_filter: !this.include_census_filter
            }

            //Call the webhook to save the new sync
            window.axios.post('/api/census/export', form_data)
                .then(response => {
                    //Make a link and then "click" on it
                    var link = document.createElement("a");
                    link.href = response.data.url;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    this.if_download_records_finished = true;
                    this.$forceUpdate();
                });
        },
        getIfExcludeFilter() {
            return !this.include_census_filter;
        },
        loadConnectionDetails(){  
            
            var url;
            //Modeled audiences have a weird data source
            if(this.isModeledAudience()) {
                url = '/api/pathway_mapping/' + this.client.url + '/' + this.local_pathway_selected.id + '/'+this.segment_selected.data_source.source+'/' + this.segment_selected.data_source.data_source_id+ '/' + this.segment_selected.id;
            }
            else if (this.segment_selected.data_source && typeof this.segment_selected.data_source === 'string') {
                url = '/api/pathway_mapping/' + this.client.url + '/' + this.local_pathway_selected.id + '/' + this.segment_selected.data_source + '/' + this.segment_selected.data_source_id + '/' + this.segment_selected.id;
            } else if (
                this.segment_selected.source &&
                (this.segment_selected.source === 'data_set' ||
                this.segment_selected.source === 'data_blend')
            ) {
                url = '/api/pathway_mapping/' + this.client.url + '/' + this.local_pathway_selected.id + '/' + this.segment_selected.source + '/' + this.segment_selected.data_source_id + '/' + this.segment_selected.id;
            } else if(this.segment_selected.datablend) {
                url = '/api/pathway_mapping/' + this.client.url + '/' + this.local_pathway_selected.id + '/data_blend/' + this.segment_selected.data_source_id+ '/' + this.segment_selected.id;
            } else {
                url = '/api/pathway_mapping/' + this.client.url + '/' + this.local_pathway_selected.id + '/data_set/' + this.segment_selected.data_source_id+ '/' + this.segment_selected.id;
            }

            let form_data = {
                ignore_filter: !this.include_census_filter
            }
            this.segment_size = null;
                
            window.axios.get(url, {params: form_data}).then(response => {
                
                this.if_advanced_setting_ready = true;
                this.local_census_connection = response.data.census_connection;
                this.all_column_mappings = response.data.census_connection.mappings;
                this.local_pathway_selected = response.data.destination;
                this.source_fields = response.data.source_fields;

                this.segment_size = this.getSize(this.segment_selected.sizes);
                this.getInstructions();

                //If there isn't a segment size already stored, look it up
                if(this.segment_size == null || this.segment_size == 0 ||
                        this.orig_include_census_filter != this.include_census_filter)
                    this.getSegmentSize();
                else if(this.local_census_connection.mappings != null) { //If there is, save primary ID so I can reference it later
                    // console.log(this.local_census_connection.mappings);
                    let pi = this.local_census_connection.mappings.find(mapping => mapping.is_primary_identifier === 1);

                    if(pi === null || pi === undefined) {
                        this.local_census_connection.mappings.unshift({
                            id: -1 * Math.random(),
                            client_id: this.client.id,
                            census_connection_id: this.local_census_connection.id,
                            source_column: this.getBlankSourceColumn(),
                            destination_column: null,
                            is_primary_identifier: true,
                        });
                    }
                    else {
                        if(!Array.isArray(pi.source_column))
                            pi.source_column = [pi.source_column];
                        this.primary_identifier = JSON.parse(JSON.stringify(pi));
                    }
                }
                this.$emit('updateCensusConnection', this.local_census_connection);

                if(!this.local_census_connection.is_file)
                    this.setUpFields();
                    
            })
            .catch(error => {
                this.if_advanced_setting_ready = true;
                this.instructions = "We couldn't load Advanced Settings. Please refresh the page and try again.";
                console.error('Failed to load pathway mapping details:', error);
            });
        },
        getInstructions() {
            // For modeled audiences, use pathway_selected.id as connection_id
            const connectionId = this.isModeledAudience() ? this.local_pathway_selected.id : this.local_census_connection.connection_id;
            const params = {};
            const dest = this.local_census_connection?.destination_object;
            if (dest != null && dest !== '') {
                params.destination_object = typeof dest === 'string' ? dest : JSON.stringify(dest);
            }
            window.axios.get('/api/pathways/' + connectionId + '/get_instructions', { params }).then(response => {
                this.instructions = response.data.instructions;
            });
        },
        setUpFields() {
            // Ensure census_destination_objects is an array
            const destinationObjects = Array.isArray(this.local_pathway_selected.census_destination_objects) 
                ? this.local_pathway_selected.census_destination_objects 
                : Object.values(this.local_pathway_selected.census_destination_objects || {});
            
            if(this.local_census_connection.destination_object !== null && this.local_census_connection.destination_object !== undefined){
                const effectiveObj = this.getEffectiveDestinationObject(this.local_census_connection.destination_object);
                for(var i = 0; i < destinationObjects.length; i++) {
                    var obj = destinationObjects[i];
                    if(obj.full_name === effectiveObj){
                        this.operations = obj.supported_operations;
                        this.destination_fields = obj.fields;
                        break;
                    }
                }
            } else if(destinationObjects.length === 1){
                this.local_census_connection.destination_object = destinationObjects[0].full_name;
                this.destination_fields = destinationObjects[0].fields;
                this.operations = destinationObjects[0].supported_operations;
                this.changeDestination();
                if (this.local_census_connection.id) {
                    this.$emit('persistDefaultedDestinationObject');
                }
            }

            this.if_expand_info_section = !this.local_census_connection.destination_object && destinationObjects.length > 1
            
            this.checkIfShowAllMappings();

            if(!destinationObjects || destinationObjects.length === 0) {
                this.getDestinationObjects();
            }
            if (this.local_pathway_selected?.integration?.display_name === 'Google Ads' &&
                this.getEffectiveDestinationObject(this.local_census_connection.destination_object) === 'enhanced_conversion') {
                this.fetchConversionActions();
            }
        },
        getEffectiveDestinationObject(obj) {
            if (!obj) return obj;
            if (obj === 'user_data' || obj === 'enhanced_conversion') return obj;
            try {
                const parsed = typeof obj === 'string' ? JSON.parse(obj) : obj;
                if (parsed && (parsed.conversion_action_id || parsed.conversionActionId)) {
                    return 'enhanced_conversion';
                }
            } catch (_) {}
            return obj;
        },
        onDestinationObjectChange(value) {
            if (value === 'enhanced_conversion') {
                this.local_census_connection.destination_object = 'enhanced_conversion';
            } else {
                this.local_census_connection.destination_object = value;
            }
            this.changeDestination();
            if (value === 'enhanced_conversion') {
                this.fetchConversionActions();
            }
            this.getInstructions();
            this.$emit('updateCensusConnection', this.local_census_connection);
        },
        onConversionActionChange(value) {
            if (!value) {
                this.local_census_connection.destination_object = 'enhanced_conversion';
            } else {
                this.local_census_connection.destination_object = JSON.stringify({ conversion_action_id: value });
            }
            this.getInstructions();
            this.$emit('updateCensusConnection', this.local_census_connection);
        },
        fetchConversionActions() {
            const connId = this.isModeledAudience() ? this.local_pathway_selected.id : this.local_census_connection.connection_id;
            if (!connId) return;
            window.axios.post('/api/census/get_dropdown_options', {
                connection_id: connId,
                column: 'enhanced_conversion.conversion_action'
            }).then(response => {
                this.conversionActions = response.data?.objects || [];
            }).catch(() => {
                this.conversionActions = [];
            });
        },
        getOperationExplanation(ops) {
            if(ops === 'mirror')
                return "Mirror - Records will added, updated, and removed to keep the sources in full sync"
            if(ops === 'append')
                return 'Append - New records will be added to the destination but existing will not be updated or removed';
            if(ops === 'upsert')
                return "Update or Create - Update existing destination records when IDs match, otherwise create new ones if they're missing"
            if(ops === 'update')
                return "Update Only - Matching records will be updated but nothing will be added or removed"
            if(ops === 'delete')
                return "Delete - Delete syncs takes the list of provided IDs and deletes them from the destination. Be careful! Providing an incorrect set of IDs can result in data loss!"
            if(ops === 'create' || ops === 'insert')
                return "Create Only - Create a new object if it doesn't exist in the destination"
        },
        changeDestination() {
            // Ensure census_destination_objects is an array
            const destinationObjects = Array.isArray(this.local_pathway_selected.census_destination_objects) 
                ? this.local_pathway_selected.census_destination_objects 
                : Object.values(this.local_pathway_selected.census_destination_objects || {});
            const effectiveObj = this.getEffectiveDestinationObject(this.local_census_connection.destination_object);
            for(var i = 0; i < destinationObjects.length; i++) {
                var obj = destinationObjects[i];
                if(obj.full_name == effectiveObj){
                    this.operations = obj.supported_operations;
                    this.destination_fields = obj.fields;
                    break;
                }
            }

            if(!this.operations){
                this.local_census_connection.operation = null;
                this.operations = [];
                this.destination_fields = [];
            }
            else if(!this.local_census_connection.operation){
                this.local_census_connection.operation = this.operations[0];
            }
            else if(this.local_census_connection.operation && !this.operations.includes(this.local_census_connection.operation)) {
                this.local_census_connection.operation = this.operations[0];
            }

            if(this.local_census_connection.destination_object && this.local_census_connection.mappings) {
                for(var i = this.local_census_connection.mappings.length-1; i >= 0; i--) {
                    let found = false;
                    for(var j = 0; j < this.destination_fields.length; j++) {
                        if(this.destination_fields[j].full_name == this.local_census_connection.mappings[i].destination_column) {
                            found = true;
                            break;
                        }
                    }
                    if(!found)
                        this.local_census_connection.mappings.splice(i, 1);
                }
            }

            if(this.$refs.column_mapping && this.local_census_connection.mappings && this.local_census_connection.mappings.length == 1)
                this.$refs.column_mapping.resetMapping();
            
            this.$emit('updateCensusConnection', this.local_census_connection);
        },
        updatePrioritization(priority) {
            this.local_census_connection.prioritization_column = priority;
            this.$emit('updateCensusConnection', this.local_census_connection);
        },
        arraysAreEqual(arr1, arr2) {
            if (arr1.length !== arr2.length) return false;
            return arr1.every((obj, index) => {
                return JSON.stringify(obj) === JSON.stringify(arr2[index]);
            });
        },
        changeMappingDetails(mappings) {
            if (mappings && mappings.length > 0) {
                // Find the primary-key mapping
                const primaryMap = mappings.find(mapping => mapping.is_primary_identifier);

                if (primaryMap) {
                    // Create a LOCAL (non-reactive) copy of the source_column array so we don’t
                    // accidentally mutate the reactive object while we inspect it.
                    const srcCols = Array.isArray(primaryMap.source_column)
                        ? [...primaryMap.source_column]
                        : [primaryMap.source_column];

                    // Remove the transient entries that accordion-multi-select pushes while typing
                    const filteredCols = srcCols.filter(col => col && col.value && col.value != null);

                    // Only proceed when the user has picked at least one REAL column
                    if (filteredCols.length > 0) {
                        // Determine if the selection has actually changed vs what we stored last time
                        const prevCols = this.primary_identifier ? this.primary_identifier.source_column : null;

                        const hasChanged = !prevCols || !this.arraysAreEqual(filteredCols, prevCols);

                        if (hasChanged) {
                            // Build a light copy of the primary identifier to persist
                            this.primary_identifier = {
                                ...primaryMap,
                                source_column: filteredCols
                            };

                            // Now that we have a real change, fetch the new record count
                            this.getSegmentSize();
                        }
                    }
                }
            }

            // Store the mappings reference (no deep modifications were made)
            this.local_census_connection.mappings = mappings;
            this.$emit('updateCensusConnection', this.local_census_connection);
        },
        getSegmentSize() {
            if(!this.local_census_connection || !this.local_census_connection.mappings || this.isModeledAudience()){
                this.segment_size = -1;
                return;
            }
            //If there are no mappings, set the primary identifier to an empty object
            let pi = Object.values(this.local_census_connection.mappings).length > 0 ? this.local_census_connection.mappings.find(mapping => mapping.is_primary_identifier) : {};
            this.segment_size = null;

            if(!Array.isArray(pi.source_column))
                pi.source_column = [pi.source_column];

            this.primary_identifier = JSON.parse(JSON.stringify(pi));

            let form_data = {
                mapping: this.primary_identifier,
                census_connection: this.local_census_connection,
                segment_id: this.segment_selected.id,
                ignore_filter: !this.include_census_filter
            }
            
            window.axios.post('/api/segment/' + this.client.url + "/size", form_data)
                .then(response => {
                    //Now I need to set a timeout to check the destination to see when it changes
                    this.segment_size = response.data.size;

                this.$nextTick(() => {
                    this.initializeTooltips();
                });
            });
        },
        getDestinationObjects() {
            
            var form_data = {
                client_id: this.client.id,
                connection_id: this.local_pathway_selected.id,
                refresh_key: this.local_pathway_selected.refresh_key
            }
            window.axios.post('/api/census/get_destination_objects', form_data)
              .then(response => {
                
                if(!response.data.success) {
                    this.if_advanced_setting_ready = false;
                    this.local_pathway_selected.refresh_key = response.data.refresh_key;
                    this.initializePusherListener();
                } else {
                    this.local_pathway_selected.census_destination_objects = response.data.objects;
                    const destinationObjects = Array.isArray(this.local_pathway_selected.census_destination_objects) 
                        ? this.local_pathway_selected.census_destination_objects 
                        : Object.values(this.local_pathway_selected.census_destination_objects || {});
                    this.if_expand_info_section = !this.local_census_connection.destination_object && destinationObjects.length > 1;
                    this.setUpFields();
                    this.$forceUpdate();
                }
              })
              .catch(error => {
              });
        },
        refreshColumns() {
            
            var form_data = {
                client_id: this.client.id,
                connection_id: this.local_pathway_selected.id,
                destination_object: this.local_census_connection.destination_object
            }
            window.axios.post('/api/census/refresh_destination', form_data)
              .then(response => {
                this.initializePusherListener();
                Swal.fire({
                    icon: 'info',
                    title: "Refreshing " + this.local_pathway_selected.integration.name,
                    html: "The refresh process has begun. This may take a few moments.",
                    customClass: {
                        popup: 'swal-info'
                    },
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    showConfirmButton: false,
                });
                
              })
              .catch(error => {
              });
        },
        allColumnsMapped() {
            if(!this.local_census_connection?.mappings || this.local_census_connection.mappings.length === 0){
                return false;
            }

            //I need to check if the first mapping is a primary identifier
            var unmappedFields = this.local_census_connection.mappings.filter((mapping, index) =>
                //If the first one isn't a primary identifier
                    (index == 0 && mapping.is_primary_identifier != 1) ||
                    //If the first one is an array but the first value isn't set
                    (index == 0 && Array.isArray(mapping.source_column) && mapping.source_column.length > 0 && (mapping.source_column[0].value == null || mapping.source_column[0].value == "")) ||
                    //If it is a normal object but the value hasn't been set
                    (mapping.source_column && !Array.isArray(mapping.source_column) && (mapping.source_column.value == null || mapping.source_column.value == ""))
            );
            return unmappedFields.length === 0;
        },
        checkIfShowAllMappings(){

            if (!this.allColumnsMapped()){
                this.if_expand_column_mapping_section = true;
                this.show_unmapped_only = true;
            } else {
                this.if_expand_column_mapping_section = false;
                this.show_unmapped_only = false;
            }
        },
        addColumnMapping(){
            this.local_census_connection.mappings.push({
                id: -1 * Math.random(),
                client_id: this.client.id,
                census_connection_id: this.local_census_connection.id,
                source_column: this.getBlankSourceColumn(),
                destination_column: null,
                is_primary_identifier: false,
            });
            this.$emit('updateCensusConnection', this.local_census_connection);
        },
        getBlankSourceColumn() {
            return {
                main_category: 'Columns',
                category: null,
                type: 'string',
                value: null,
                text: ""
            };
        },
        initializePusherListener() {
            // Join the private channel for this client
            window.Echo.private('client.' + this.client.id)
                .listen('DestinationObjectsRefreshed', (e) => {
                    if (e.connection_id === this.local_pathway_selected.id) {
                        //Get the connection info using the connection_id
                        window.axios.get('/api/connection/' + e.connection_id).then(response => {
                            this.local_pathway_selected.census_destination_objects = response.data.connection.census_destination_objects;
                            
                            this.if_advanced_setting_ready = true;
                            this.setUpFields();
                            this.$forceUpdate();
                        });
                            
                        
                        // Close the Swal dialog if it's open
                        if (window.Swal && window.Swal.isVisible()) {
                            
                            window.Swal.close();
                        }
                    } else {
                    }
                });
        },
        shouldShowRefreshButton() {
            // Check if the integration name is Google Ads or Mailchimp
            if (!this.local_pathway_selected || !this.local_pathway_selected.integration) {
                return false;
            }
            
            const integrationName = this.local_pathway_selected.integration.name;
            return integrationName === 'Google Ads' || integrationName === 'Mailchimp';
        },
    }
})
</script>
